import {
  DefaultUsersTab,
  IntakeApproversTab,
  LimitedListTab,
  ProjectTeamTab,
} from "@/containers/Admin/ProjectRoles/components/RegionalRole/Tabs";
import { IRegionalProjectRole } from "@/interfaces/Project/Roles/IRegionalProjectRole";

import { RedIcon } from "@/components/styled";
import { FieldNames, FormType } from "@/enums";
import { useFormPropertyValueLookupProvider } from "@/providers";
import { faTrash } from "@fortawesome/pro-solid-svg-icons";

import { nameof } from "@/helpers";
import { IProjectRole } from "@/interfaces";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { Badge, Button, Card, Popconfirm, Space, Tabs } from "antd";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";

interface IProps {
  role: IProjectRole;
  regionRole: IRegionalProjectRole;
  deleteRole(id: string): void;
}

export const RegionalRoleEditor = ({ regionRole, deleteRole }: IProps) => {
  const intl = useIntl();
  const { getPropertyValueDisplay } = useFormPropertyValueLookupProvider();

  const [title, setTitle] = useState<string>("");

  useEffect(() => {
    if (!regionRole) return;

    const region = getPropertyValueDisplay(
      FormType.Intake,
      FieldNames.Region,
      regionRole.region
    );

    const functionalArea = getPropertyValueDisplay(
      FormType.Intake,
      FieldNames.FunctionalArea,
      regionRole.functionalArea
    );

    setTitle(`${region} - ${functionalArea}`);
  }, [regionRole, getPropertyValueDisplay]);

  const tabItems = [
    {
      label: (
        <Space>
          {intl.formatMessage({ id: "projectRoles.projectTeamTab" })}
          <Badge showZero count={regionRole?.projectTeams?.length} />
        </Space>
      ),
      key: nameof<IRegionalProjectRole>("projectTeams"),
      children: <ProjectTeamTab regionRole={regionRole} />,
    },
    {
      label: (
        <Space>
          {intl.formatMessage({ id: "projectRoles.defaultUsersTab" })}
          <Badge showZero count={regionRole?.defaultProjectUsers?.length} />
        </Space>
      ),
      key: nameof<IRegionalProjectRole>("defaultProjectUsers"),
      children: <DefaultUsersTab regionRole={regionRole} />,
    },
    {
      label: (
        <Space>
          {intl.formatMessage({ id: "projectRoles.limitedListTab" })}
          <Badge showZero count={regionRole?.limitedListUsers?.length} />
        </Space>
      ),
      key: nameof<IRegionalProjectRole>("limitedListUsers"),
      children: <LimitedListTab regionRole={regionRole} />,
    },
    {
      label: (
        <Space>
          {intl.formatMessage({ id: "projectRoles.intakeApproversTab" })}
          <Badge showZero count={regionRole?.intakeApprovers?.length} />
        </Space>
      ),
      key: nameof<IRegionalProjectRole>("intakeApprovers"),
      children: <IntakeApproversTab regionRole={regionRole} />,
    },
  ];

  return (
    <Card
      title={title}
      className="w-full mt-2"
      extra={
        <Popconfirm
          placement="topRight"
          title={intl.formatMessage({
            id: "projectRoles.deleteRegionalRoleTitle",
          })}
          description={intl.formatMessage({
            id: "projectRoles.deleteRegionalRoleConfirm",
          })}
          onConfirm={(): void => deleteRole(regionRole?.id)}
        >
          <Button
            data-test-id={`delete-regional-role-${regionRole?.id}`}
            type="link"
            icon={<RedIcon icon={faTrash as IconProp} />}
          />
        </Popconfirm>
      }
    >
      <Tabs defaultActiveKey="projectTeam" items={tabItems} />
    </Card>
  );
};
